import { modifierPriceAdjFn } from '@/util/functions/data/modifierPriceAdjFn';
import { AddressForScheduler } from '@/util/types/globalTypes';
import {
  Charge,
  EquipmentQuantity,
  RequiredInfoValue,
  ServWithMods,
} from '@/util/types/serverTypes';
import {
  serviceHasPropertyDrivenModifiers,
  serviceHasRequiredInfoModifiers,
  serviceModifiersTouchRequiredInfoIds,
} from './requiredInfoModifierUtils';
import {
  primaryServiceBaseDuration,
} from './serviceDurationHelpers';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

const serviceHasRequestedInspectorModifiers = (service: ServWithMods): boolean =>
  service.modifiers?.some(
    (mod) =>
      mod.modType === 'requested' || mod.modiferData?.basis === 'requested'
  ) ?? false;

/**
 * Per-line workorder recalculation uses `modifierPriceAdjFn` with the same property /
 * requiredInfoValues / equipment / inspector inputs as {@link calculateServicePrices}
 * (used by NewSchedulingForm via usePriceCalculation). Stored bundle/code **dollar**
 * amounts on each charge are held fixed here; full multi-line bundle recomputation would
 * need the live Bundle/DiscountCode objects and calculateServicePrices.
 */

export type RecalculateChargesForRequiredInfoOptions = {
  /**
   * When set, only charges whose modifiers reference at least one of these
   * required-info field ids are recalculated (others unchanged).
   */
  requiredInfoIdsChanged?: Set<string>;
};

export interface ChargeRecalculationUpdate {
  chargeId: string;
  newAmount: number;
  newPreTaxAmount: number;
  newModifiersAmount: number;
}

/**
 * Recalculates charge amounts for services whose pricing depends on required info values.
 *
 * Rules:
 * - Only processes charges for services with required-info-based modifiers
 * - Skips charges without `preTaxAmount` stored (can't safely detect manual overrides)
 * - Skips charges with a manual price override (amount ≠ preTaxAmount + tax)
 * - Preserves existing bundle/code discount amounts as fixed values
 * - Only returns charges where the price actually changed
 *
 * Note: For percentage-based bundle/code discounts, the stored dollar amounts are
 * treated as fixed. This is accurate for the common case (no bundle). If modifiers
 * change significantly on a bundled charge, the user can always adjust via
 * Edit Charges.
 */
export function recalculateChargesForRequiredInfo(
  charges: Charge[],
  serviceDefinitions: ServWithMods[],
  newRequiredInfoValues: RequiredInfoValue[],
  property: AddressForScheduler,
  inspectorIds: string[] = [],
  equipmentQuantities: EquipmentQuantity[] = [],
  options?: RecalculateChargesForRequiredInfoOptions,
  requested = false
): ChargeRecalculationUpdate[] {
  const updates: ChargeRecalculationUpdate[] = [];

  const filter = options?.requiredInfoIdsChanged;
  if (filter !== undefined && filter.size === 0) {
    return [];
  }

  const serviceIds = charges.map((c) =>
    typeof c._serviceId === 'object'
      ? (c._serviceId as any)._id
      : String(c._serviceId)
  );

  for (const charge of charges) {
    // Require preTaxAmount to be stored — without it we can't reliably detect
    // manual price overrides and might clobber user-entered amounts
    if (charge.preTaxAmount === undefined || charge.preTaxAmount === null) {
      continue;
    }

    const serviceId =
      typeof charge._serviceId === 'object'
        ? (charge._serviceId as any)._id
        : String(charge._serviceId);

    const serviceDef = serviceDefinitions.find((s) => s._id === serviceId);
    if (!serviceDef) continue;

    // Only recalculate services whose price is driven by required info
    if (!serviceHasRequiredInfoModifiers(serviceDef)) continue;

    if (
      filter !== undefined &&
      !serviceModifiersTouchRequiredInfoIds(serviceDef, filter)
    ) {
      continue;
    }

    // Detect manual price override:
    // Auto-calculated: amount == preTaxAmount + tax (within rounding)
    // Manually overridden: amount differs → skip to preserve user's edit
    const storedTotal = round2(
      (charge.preTaxAmount ?? 0) + (charge.tax ?? 0)
    );
    const isManualOverride = Math.abs(charge.amount - storedTotal) > 0.01;
    if (isManualOverride) continue;

    // Compute new modifier adjustments with updated required info values
    const isPrimary = charge.isPrimary ?? false;
    const startingFee = isPrimary
      ? (serviceDef as any).isPrimaryFee ?? serviceDef.fee ?? 0
      : serviceDef.fee ?? 0;

    const mods = modifierPriceAdjFn(
      { ...serviceDef, startingFee, isPrimary },
      property,
      requested,
      newRequiredInfoValues,
      equipmentQuantities,
      serviceIds,
      inspectorIds
    );

    // Skip this charge if modifier calculation returns an error
    if ('error' in mods) continue;

    const bundleDisc = charge.bundleDiscountAmount ?? 0;
    const codeDisc = charge.codeDiscountAmount ?? 0;

    // New pre-discount base = starting fee + new modifier total
    const newBase = round2(startingFee + mods.finalPrice);

    // Apply existing discount amounts as fixed values (preserves bundle/code discounts)
    const newPreTaxAmount = round2(newBase - bundleDisc - codeDisc);

    // Recalculate tax on the new post-discount pre-tax amount
    let newTax = 0;
    if (serviceDef.tax && serviceDef.tax > 0) {
      if (serviceDef.taxType === 'percent') {
        newTax = round2(newPreTaxAmount * serviceDef.tax);
      } else {
        newTax = Math.min(newPreTaxAmount, round2(serviceDef.tax));
      }
    }

    const newAmount = round2(newPreTaxAmount + newTax);

    // Only queue an update when the amount actually changed
    if (Math.abs(newAmount - charge.amount) > 0.01) {
      updates.push({
        chargeId: String(charge._id),
        newAmount,
        newPreTaxAmount,
        newModifiersAmount: mods.finalPrice,
      });
    }
  }

  return updates;
}

/**
 * Recompute charge lines whose modifiers depend on property fields (sqft, age, zip, …).
 * Same rules as {@link recalculateChargesForRequiredInfo} (manual overrides, bundle/code $ fixed).
 */
export function recalculateChargesForPropertyChange(
  charges: Charge[],
  serviceDefinitions: ServWithMods[],
  requiredInfoValues: RequiredInfoValue[],
  newProperty: AddressForScheduler,
  inspectorIds: string[] = [],
  equipmentQuantities: EquipmentQuantity[] = [],
  requested = false
): ChargeRecalculationUpdate[] {
  const updates: ChargeRecalculationUpdate[] = [];

  const serviceIds = charges.map((c) =>
    typeof c._serviceId === 'object'
      ? (c._serviceId as any)._id
      : String(c._serviceId)
  );

  for (const charge of charges) {
    if (charge.preTaxAmount === undefined || charge.preTaxAmount === null) {
      continue;
    }

    const serviceId =
      typeof charge._serviceId === 'object'
        ? (charge._serviceId as any)._id
        : String(charge._serviceId);

    const serviceDef = serviceDefinitions.find((s) => s._id === serviceId);
    if (!serviceDef) continue;

    if (!serviceHasPropertyDrivenModifiers(serviceDef)) continue;

    const storedTotal = round2(
      (charge.preTaxAmount ?? 0) + (charge.tax ?? 0)
    );
    const isManualOverride = Math.abs(charge.amount - storedTotal) > 0.01;
    if (isManualOverride) continue;

    const isPrimary = charge.isPrimary ?? false;
    const startingFee = isPrimary
      ? (serviceDef as any).isPrimaryFee ?? serviceDef.fee ?? 0
      : serviceDef.fee ?? 0;

    const mods = modifierPriceAdjFn(
      { ...serviceDef, startingFee, isPrimary },
      newProperty,
      requested,
      requiredInfoValues,
      equipmentQuantities,
      serviceIds,
      inspectorIds
    );

    if ('error' in mods) continue;

    const bundleDisc = charge.bundleDiscountAmount ?? 0;
    const codeDisc = charge.codeDiscountAmount ?? 0;

    const newBase = round2(startingFee + mods.finalPrice);

    const newPreTaxAmount = round2(newBase - bundleDisc - codeDisc);

    let newTax = 0;
    if (serviceDef.tax && serviceDef.tax > 0) {
      if (serviceDef.taxType === 'percent') {
        newTax = round2(newPreTaxAmount * serviceDef.tax);
      } else {
        newTax = Math.min(newPreTaxAmount, round2(serviceDef.tax));
      }
    }

    const newAmount = round2(newPreTaxAmount + newTax);

    if (Math.abs(newAmount - charge.amount) > 0.01) {
      updates.push({
        chargeId: String(charge._id),
        newAmount,
        newPreTaxAmount,
        newModifiersAmount: mods.finalPrice,
      });
    }
  }

  return updates;
}

export function recalculateChargesForRequestedInspector(
  charges: Charge[],
  serviceDefinitions: ServWithMods[],
  requiredInfoValues: RequiredInfoValue[],
  property: AddressForScheduler,
  inspectorIds: string[] = [],
  equipmentQuantities: EquipmentQuantity[] = [],
  requested = false
): ChargeRecalculationUpdate[] {
  const updates: ChargeRecalculationUpdate[] = [];

  const serviceIds = charges.map((c) =>
    typeof c._serviceId === 'object'
      ? (c._serviceId as any)._id
      : String(c._serviceId)
  );

  for (const charge of charges) {
    if (charge.preTaxAmount === undefined || charge.preTaxAmount === null) {
      continue;
    }

    const serviceId =
      typeof charge._serviceId === 'object'
        ? (charge._serviceId as any)._id
        : String(charge._serviceId);

    const serviceDef = serviceDefinitions.find((s) => s._id === serviceId);
    if (!serviceDef || !serviceHasRequestedInspectorModifiers(serviceDef)) {
      continue;
    }

    const storedTotal = round2(
      (charge.preTaxAmount ?? 0) + (charge.tax ?? 0)
    );
    const isManualOverride = Math.abs(charge.amount - storedTotal) > 0.01;
    if (isManualOverride) continue;

    const isPrimary = charge.isPrimary ?? false;
    const startingFee = isPrimary
      ? (serviceDef as any).isPrimaryFee ?? serviceDef.fee ?? 0
      : serviceDef.fee ?? 0;

    const mods = modifierPriceAdjFn(
      { ...serviceDef, startingFee, isPrimary },
      property,
      requested,
      requiredInfoValues,
      equipmentQuantities,
      serviceIds,
      inspectorIds
    );

    if ('error' in mods) continue;

    const bundleDisc = charge.bundleDiscountAmount ?? 0;
    const codeDisc = charge.codeDiscountAmount ?? 0;
    const newBase = round2(startingFee + mods.finalPrice);
    const newPreTaxAmount = round2(newBase - bundleDisc - codeDisc);

    let newTax = 0;
    if (serviceDef.tax && serviceDef.tax > 0) {
      if (serviceDef.taxType === 'percent') {
        newTax = round2(newPreTaxAmount * serviceDef.tax);
      } else {
        newTax = Math.min(newPreTaxAmount, round2(serviceDef.tax));
      }
    }

    const newAmount = round2(newPreTaxAmount + newTax);

    if (Math.abs(newAmount - charge.amount) > 0.01) {
      updates.push({
        chargeId: String(charge._id),
        newAmount,
        newPreTaxAmount,
        newModifiersAmount: mods.finalPrice,
      });
    }
  }

  return updates;
}

export type AffectedChargeLinePreview = {
  chargeId: string;
  name: string;
  oldAmount: number;
  newAmount: number;
};

export type RequiredInfoPricePreview = {
  updates: ChargeRecalculationUpdate[];
  /** Subtotal for updated lines only */
  affectedSubtotalOld: number;
  affectedSubtotalNew: number;
  /** Full job (all charges) before/after applying updates */
  jobTotalOld: number;
  jobTotalNew: number;
  affectedLines: AffectedChargeLinePreview[];
  /** Sum of per-line durations (modifier time + base service duration), same basis as calculateServicePrices */
  jobDurationHoursOld: number;
  jobDurationHoursNew: number;
};

/**
 * Total scheduled hours for all charges: sum of (modifier finalDuration + base duration)
 * per service, matching {@link calculateServicePrices}.
 */
export function computeTotalJobDurationHours(
  charges: Charge[],
  serviceDefinitions: ServWithMods[],
  property: AddressForScheduler,
  requiredInfoValues: RequiredInfoValue[],
  equipmentQuantities: EquipmentQuantity[],
  inspectorIds: string[] = [],
  requested = false
): number {
  const serviceIds = charges.map((c) =>
    typeof c._serviceId === 'object'
      ? (c._serviceId as any)._id
      : String(c._serviceId)
  );

  let sum = 0;
  for (const charge of charges) {
    const serviceId =
      typeof charge._serviceId === 'object'
        ? (charge._serviceId as any)._id
        : String(charge._serviceId);
    const serviceDef = serviceDefinitions.find((s) => s._id === serviceId);
    if (!serviceDef) continue;

    const isPrimary = charge.isPrimary ?? false;
    const startingFee = isPrimary
      ? (serviceDef as any).isPrimaryFee ?? serviceDef.fee ?? 0
      : serviceDef.fee ?? 0;

    const mods = modifierPriceAdjFn(
      { ...serviceDef, startingFee, isPrimary },
      property,
      requested,
      requiredInfoValues,
      equipmentQuantities,
      serviceIds,
      inspectorIds
    );

    const modDur =
      !('error' in mods) && 'finalDuration' in mods ? mods.finalDuration : 0;
    const baseDur = isPrimary
      ? primaryServiceBaseDuration(
          serviceDef.duration,
          (serviceDef as { durationAsPrimary?: number | null }).durationAsPrimary,
        )
      : serviceDef.duration;

    // Match usePriceCalculation: services with autoEvents don't add to job duration total
    if ((serviceDef as { autoEvents?: { length: number } }).autoEvents?.length) {
      continue;
    }

    sum += modDur + baseDur;
  }

  return round2(sum);
}

function buildPricePreviewFromUpdates(
  charges: Charge[],
  updates: ChargeRecalculationUpdate[],
  jobDurationHoursOld: number,
  jobDurationHoursNew: number
): RequiredInfoPricePreview {
  const affectedLines: AffectedChargeLinePreview[] = updates.map((u) => {
    const c = charges.find((x) => String(x._id) === u.chargeId);
    return {
      chargeId: u.chargeId,
      name: c?.name ?? `Charge ${u.chargeId}`,
      oldAmount: round2(Number(c?.amount) || 0),
      newAmount: u.newAmount,
    };
  });

  const affectedSubtotalOld = round2(
    affectedLines.reduce((s, l) => s + l.oldAmount, 0)
  );
  const affectedSubtotalNew = round2(
    affectedLines.reduce((s, l) => s + l.newAmount, 0)
  );

  const jobTotalOld = round2(
    charges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0)
  );

  const idToAmount = new Map<string, number>(
    charges.map((c) => [String(c._id), Number(c.amount) || 0])
  );
  for (const u of updates) {
    idToAmount.set(u.chargeId, u.newAmount);
  }
  const jobTotalNew = round2(
    [...idToAmount.values()].reduce((a, b) => a + b, 0)
  );

  return {
    updates,
    affectedSubtotalOld,
    affectedSubtotalNew,
    jobTotalOld,
    jobTotalNew,
    affectedLines,
    jobDurationHoursOld,
    jobDurationHoursNew,
  };
}

/** Totals + charge deltas if required info were saved (for confirmation UI). */
export function previewRequiredInfoPriceChange(
  charges: Charge[],
  serviceDefinitions: ServWithMods[],
  newRequiredInfoValues: RequiredInfoValue[],
  property: AddressForScheduler,
  inspectorIds: string[] = [],
  equipmentQuantities: EquipmentQuantity[] = [],
  options?: RecalculateChargesForRequiredInfoOptions,
  previousRequiredInfoValues?: RequiredInfoValue[]
): RequiredInfoPricePreview {
  const updates = recalculateChargesForRequiredInfo(
    charges,
    serviceDefinitions,
    newRequiredInfoValues,
    property,
    inspectorIds,
    equipmentQuantities,
    options
  );
  const prevRI = previousRequiredInfoValues ?? newRequiredInfoValues;
  const jobDurationHoursOld = computeTotalJobDurationHours(
    charges,
    serviceDefinitions,
    property,
    prevRI,
    equipmentQuantities,
    inspectorIds,
    false
  );
  const jobDurationHoursNew = computeTotalJobDurationHours(
    charges,
    serviceDefinitions,
    property,
    newRequiredInfoValues,
    equipmentQuantities,
    inspectorIds,
    false
  );
  return buildPricePreviewFromUpdates(
    charges,
    updates,
    jobDurationHoursOld,
    jobDurationHoursNew
  );
}

/** Same preview shape as required info, for property updates on the workorder. */
export function previewPropertyPriceChange(
  charges: Charge[],
  serviceDefinitions: ServWithMods[],
  requiredInfoValues: RequiredInfoValue[],
  newProperty: AddressForScheduler,
  inspectorIds: string[] = [],
  equipmentQuantities: EquipmentQuantity[] = [],
  previousProperty?: AddressForScheduler
): RequiredInfoPricePreview {
  const updates = recalculateChargesForPropertyChange(
    charges,
    serviceDefinitions,
    requiredInfoValues,
    newProperty,
    inspectorIds,
    equipmentQuantities,
    false
  );
  const prevP = previousProperty ?? newProperty;
  const jobDurationHoursOld = computeTotalJobDurationHours(
    charges,
    serviceDefinitions,
    prevP,
    requiredInfoValues,
    equipmentQuantities,
    inspectorIds,
    false
  );
  const jobDurationHoursNew = computeTotalJobDurationHours(
    charges,
    serviceDefinitions,
    newProperty,
    requiredInfoValues,
    equipmentQuantities,
    inspectorIds,
    false
  );
  return buildPricePreviewFromUpdates(
    charges,
    updates,
    jobDurationHoursOld,
    jobDurationHoursNew
  );
}

export function previewRequestedInspectorPriceChange(
  charges: Charge[],
  serviceDefinitions: ServWithMods[],
  requiredInfoValues: RequiredInfoValue[],
  property: AddressForScheduler,
  inspectorIds: string[] = [],
  equipmentQuantities: EquipmentQuantity[] = [],
  previousRequested = false,
  nextRequested = false
): RequiredInfoPricePreview {
  const updates = recalculateChargesForRequestedInspector(
    charges,
    serviceDefinitions,
    requiredInfoValues,
    property,
    inspectorIds,
    equipmentQuantities,
    nextRequested
  );
  const jobDurationHoursOld = computeTotalJobDurationHours(
    charges,
    serviceDefinitions,
    property,
    requiredInfoValues,
    equipmentQuantities,
    inspectorIds,
    previousRequested
  );
  const jobDurationHoursNew = computeTotalJobDurationHours(
    charges,
    serviceDefinitions,
    property,
    requiredInfoValues,
    equipmentQuantities,
    inspectorIds,
    nextRequested
  );

  return buildPricePreviewFromUpdates(
    charges,
    updates,
    jobDurationHoursOld,
    jobDurationHoursNew
  );
}
